import { resolveFormSchemaIdFromPageEntry } from '@/common/utils/resolvePageIdentity'
import { resolveSameWindowAuthoritativeFormId } from '@/common/utils/sameWindowFormRefetch'

/**
 * Состояния из ответа executeScript / action: корень `states` или `jsResult.states` (onLoad / onClick / dialogClosed).
 */
export function normalizeExchangeStates(responseData: unknown): any[] {
  if (responseData == null || typeof responseData !== 'object' || Array.isArray(responseData)) {
    return []
  }
  const r = responseData as Record<string, any>
  const root = r.states
  const nested = r.jsResult?.states
  if (Array.isArray(root) && root.length > 0) {
    return root
  }
  if (Array.isArray(nested) && nested.length > 0) {
    return nested
  }
  if (Array.isArray(root)) {
    return root
  }
  if (Array.isArray(nested)) {
    return nested
  }
  return []
}

function exchangeStateLevel(state: Record<string, any>): string {
  return String(state.element_level ?? state.element_type ?? '').toUpperCase()
}

function exchangeStateId(state: Record<string, any>): string {
  const raw = state.element_id
  if (raw == null) return ''
  const id = String(raw).trim()
  return id
}

function exchangeStateName(state: Record<string, any>): string {
  return String(state.element_name ?? '').trim()
}

/** Registry keys `field#xmlId` must not persist as a distinct ELEMENT::<name#id> merge row. */
export function splitRegistryElementPersistName(name: string): {
  fieldName: string
  xmlId: string | null
} {
  const text = String(name ?? '').trim()
  const match = text.match(/^(.+)#(\d+)$/)
  if (!match) return { fieldName: text, xmlId: null }
  return { fieldName: match[1], xmlId: match[2] }
}

function normalizeExchangeStatePersistName(state: Record<string, any>): Record<string, any> {
  const name = exchangeStateName(state)
  const split = splitRegistryElementPersistName(name)
  if (!split.xmlId) return state
  const out = { ...state, element_name: split.fieldName }
  if (!exchangeStateId(out)) {
    const asNumber = Number(split.xmlId)
    out.element_id = Number.isFinite(asNumber) && asNumber > 0 ? asNumber : split.xmlId
  }
  return out
}

/**
 * One visual entity: id beats name so client setVisible(true) replaces onLoad is_visible:false
 * even when the persist patch omits element_name (`ELEMENT:43025:` vs `ELEMENT:43025:extraFiles`).
 */
export function exchangeStateMergeKey(state: Record<string, any> | null | undefined): string {
  if (state == null || typeof state !== 'object') return ''
  const level = exchangeStateLevel(state)
  const id = exchangeStateId(state)
  if (id) return `${level}:${id}`
  const name = exchangeStateName(state)
  if (name) return `${level}::${name}`
  return level ? `${level}:` : ''
}

function exchangeStateNameAliasKey(state: Record<string, any> | null | undefined): string {
  if (state == null || typeof state !== 'object') return ''
  const name = exchangeStateName(state)
  if (!name) return ''
  return `${exchangeStateLevel(state)}::${name}`
}

export function mergeExchangeStatesByKey(previous: any[] | null | undefined, incoming: any[] | null | undefined): any[] {
  const prev = Array.isArray(previous) ? [...previous] : []
  const inc = Array.isArray(incoming) ? incoming : []
  if (inc.length === 0) {
    return prev
  }
  if (prev.length === 0) {
    return inc.map((s) => (s && typeof s === 'object' ? { ...s } : s))
  }

  const indexByKey = new Map<string, number>()
  const indexState = (s: Record<string, any>, i: number) => {
    const k = exchangeStateMergeKey(s)
    if (k) indexByKey.set(k, i)
    const nameAlias = exchangeStateNameAliasKey(s)
    if (nameAlias && !indexByKey.has(nameAlias)) indexByKey.set(nameAlias, i)
    const split = splitRegistryElementPersistName(exchangeStateName(s))
    const level = exchangeStateLevel(s)
    const id = exchangeStateId(s)
    if (!id && split.xmlId) {
      const idKey = `${level}:${split.xmlId}`
      if (!indexByKey.has(idKey)) indexByKey.set(idKey, i)
      const fieldKey = `${level}::${split.fieldName}`
      if (!indexByKey.has(fieldKey)) indexByKey.set(fieldKey, i)
    }
    if (id) {
      const field = split.xmlId ? split.fieldName : exchangeStateName(s)
      if (field) {
        const hashKey = `${level}::${field}#${id}`
        if (!indexByKey.has(hashKey)) indexByKey.set(hashKey, i)
      }
    }
  }
  prev.forEach((s, i) => {
    if (s && typeof s === 'object') {
      indexState(s as Record<string, any>, i)
    }
  })

  const mergeOne = (base: Record<string, any>, patch: Record<string, any>): Record<string, any> => {
    const out = { ...base }
    for (const key of Object.keys(patch)) {
      const v = patch[key]
      if (v !== undefined) {
        out[key] = v
      }
    }
    return out
  }

  for (const raw of inc) {
    if (raw == null || typeof raw !== 'object') {
      continue
    }
    const patch = normalizeExchangeStatePersistName(raw as Record<string, any>)
    const k = exchangeStateMergeKey(patch)
    if (!k) {
      prev.push({ ...patch })
      continue
    }
    const idx = indexByKey.get(k) ?? indexByKey.get(exchangeStateNameAliasKey(patch))
    if (idx !== undefined) {
      const merged = normalizeExchangeStatePersistName(
        mergeOne(prev[idx] as Record<string, any>, patch),
      )
      prev[idx] = merged
      indexState(merged, idx)
    } else {
      const nextIndex = prev.length
      prev.push({ ...patch })
      indexState(patch, nextIndex)
    }
  }
  return collapseRegistryHashAliasStates(prev)
}

function collapseRegistryHashAliasStates(states: any[]): any[] {
  const ids = new Set<string>()
  for (const s of states) {
    if (!s || typeof s !== 'object') continue
    if (exchangeStateLevel(s as Record<string, any>) !== 'ELEMENT') continue
    const id = exchangeStateId(s as Record<string, any>)
    if (id && /^\d+$/.test(id)) ids.add(id)
  }
  if (ids.size === 0) return states
  return states.filter((s) => {
    if (!s || typeof s !== 'object') return true
    const rec = s as Record<string, any>
    if (exchangeStateLevel(rec) !== 'ELEMENT') return true
    if (exchangeStateId(rec)) return true
    const split = splitRegistryElementPersistName(exchangeStateName(rec))
    return !(split.xmlId && ids.has(split.xmlId))
  })
}

function resolveExchangeStatesSchemaId(pageData: Record<string, any> | null | undefined): string {
  if (!pageData) return ''
  const jsFormId = pageData.jsResult?.form_id
  if (jsFormId != null) {
    const normalized = String(jsFormId).trim()
    if (normalized && normalized !== '0') return normalized
  }
  const fromPage = resolveFormSchemaIdFromPageEntry(pageData, '')
  return fromPage > 0 ? String(fromPage) : ''
}

/** не тащить is_editable дочерней формы через merge по element_id. */
export function exchangeStatesSchemaChanged(
  currentPageData: Record<string, any> | null | undefined,
  response: Record<string, any> | null | undefined,
): boolean {
  const oldId = resolveExchangeStatesSchemaId(currentPageData)
  if (!oldId) return false

  const r = response && typeof response === 'object' ? response : null
  let newId =
    r?.jsResult?.form_id != null ? String(r.jsResult.form_id).trim() : ''
  if (!newId || newId === '0') {
    newId = resolveSameWindowAuthoritativeFormId(r)
  }
  if (!newId || newId === '0') {
    newId = resolveExchangeStatesSchemaId(r as Record<string, any>)
  }

  return newId !== '' && oldId !== newId
}

export function mergeExchangeStatesForPageUpdate(
  response: Record<string, any> | null | undefined,
  currentPageData: Record<string, any> | null | undefined,
): any[] {
  const normalized = normalizeExchangeStates(response)
  const prev = currentPageData?.jsResult?.states
  const prevArr = Array.isArray(prev) ? prev : []

  const r = response && typeof response === 'object' ? response : null

  if (exchangeStatesSchemaChanged(currentPageData, response)) {
    if (normalized.length > 0) {
      return [...normalized]
    }
    if (
      r?.jsResult &&
      typeof r.jsResult === 'object' &&
      Object.prototype.hasOwnProperty.call(r.jsResult, 'states')
    ) {
      return Array.isArray(r.jsResult.states) ? [...r.jsResult.states] : []
    }
    if (r && Object.prototype.hasOwnProperty.call(r, 'states')) {
      return Array.isArray(r.states) ? [...r.states] : []
    }
    return []
  }

  if (normalized.length > 0) {
    return prevArr.length > 0 ? mergeExchangeStatesByKey(prevArr, normalized) : [...normalized]
  }

  if (
    r?.jsResult &&
    typeof r.jsResult === 'object' &&
    'states' in r.jsResult &&
    Array.isArray((r.jsResult as Record<string, any>).states)
  ) {
    return (r.jsResult as Record<string, any>).states
  }
  if (r && 'states' in r && Array.isArray(r.states)) {
    return r.states
  }
  return prevArr
}
